import {
  Button,
  FormAsyncAutocomplete,
  FormTextField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createFileRoute } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { SupplierSapData } from "../../../../@types/services/Vendor";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { fetchSegments } from "../../../../services/segments";
import {
  createVendor,
  fetchSupplierSapData,
  fetchVendorSapDetails,
} from "../../../../services/vendors";

const formSchema = z.object({
  address_number: z.optional(z.string()),
  address_suppl: z.optional(z.string()),
  address1: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  country: z.string().min(1, "Country is required"),
  descriptionofservices: z.optional(z.string()),
  district: z.optional(z.string()),
  email: z.email(),
  fax: z.optional(z.string()),
  fullvendorname: z.string().min(1, "Full vendor name is required"),
  phone: z.optional(z.string()),
  pobox: z.optional(z.string()),
  popostalcode: z.optional(z.string()),
  postalcode: z.optional(z.string()),
  sap_code: z.optional(z.string()),
  segment: z
    .object({
      created_at: z.string(),
      id: z.number(),
      name: z.string(),
      updated_at: z.string(),
    })
    .nullable()
    .optional(),
  segment2: z
    .object({
      created_at: z.string(),
      id: z.number(),
      name: z.string(),
      updated_at: z.string(),
    })
    .nullable()
    .optional(),
  state_old: z.optional(z.string()),
  taxnumber1: z.string().min(1, "Tax number is required"),
  website: z.optional(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

const defaultValues: FormValues = {
  address_number: "",
  address_suppl: "",
  address1: "",
  city: "",
  country: "",
  descriptionofservices: "",
  district: "",
  email: "",
  fax: "",
  fullvendorname: "",
  phone: "",
  pobox: "",
  popostalcode: "",
  postalcode: "",
  sap_code: "",
  segment: null,
  segment2: null,
  state_old: "",
  taxnumber1: "",
  website: "",
};

const RouteComponent: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    (data) => {
      createVendor({
        ...data,
        segment: data.segment?.name || "",
        segment2: data.segment2?.name || "",
      })
        .then((response) => {
          enqueueSnackbar("Vendor registered successfully!", {
            variant: "success",
          });
          // TODO: Change redirect to use router after vendor detail page is ready
          window.location.href = `/vsupplierregistration/vendors/${response.id}`;
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            const errorMessage = error.response.data.errors.join(", ");
            enqueueSnackbar(errorMessage, {
              variant: "error",
            });
          } else {
            enqueueSnackbar("Failed to create vendor", {
              variant: "error",
            });
          }
        });
    },
    [enqueueSnackbar],
  );

  const handleVendorSelect = useCallback(
    (selectedVendor: SupplierSapData) => {
      setFormLoading(true);
      fetchVendorSapDetails(selectedVendor.Supplier)
        .then((response) => {
          reset({ ...defaultValues, ...response });
          setShowForm(true);
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            enqueueSnackbar("Supplier not found", {
              variant: "warning",
            });
            return;
          }

          if (error.response?.status === 409) {
            enqueueSnackbar("Supplier already registered", {
              variant: "warning",
            });
            return;
          }

          enqueueSnackbar("Failed to fetch vendor data", {
            variant: "error",
          });
        })
        .finally(() => setFormLoading(false));
    },
    [enqueueSnackbar, reset],
  );

  const { control: supplierSearchControl, watch: supplierSearchWatch } =
    useForm<{
      supplierSearch: SupplierSapData | null;
    }>({
      defaultValues: { supplierSearch: null },
    });

  const supplierSearch = supplierSearchWatch("supplierSearch");

  useEffect(() => {
    if (supplierSearch) {
      handleVendorSelect(supplierSearch);
    } else {
      setShowForm(false);
      reset(defaultValues);
    }
  }, [handleVendorSelect, reset, supplierSearch]);

  return (
    <PageContainer
      showBackButton
      title={t("vsupplierregistration.vendors.catalog.title")}
    >
      <Paper
        sx={{
          borderRadius: 1,
          boxShadow: theme.shadows[1],
          mb: 3,
          overflow: "hidden",
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            <Box>
              <FormAsyncAutocomplete
                control={supplierSearchControl}
                fetchOptions={fetchSupplierSapData}
                filters={(searchTerm: string) => ({
                  searchTerm,
                })}
                getOptionLabel={(option) =>
                  `${option.Name} (${option.Supplier} | ${option.VATRegistration})`
                }
                label={t(
                  "vsupplierregistration.vendors.catalog.chooseVendorFromList",
                )}
                minSearchLength={3}
                name="supplierSearch"
                placeholder={t(
                  "vsupplierregistration.vendors.catalog.typeVendorNameSapCodeOrTaxNumberToSearch",
                )}
                queryKey={["sapSuppliers", "infinity"]}
              />

              <Typography
                sx={{ color: "text.secondary", mt: 2 }}
                variant="body2"
              >
                {t("vsupplierregistration.vendors.catalog.obsChooseVendor")}
              </Typography>
            </Box>

            {formLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              showForm && (
                <form
                  data-testid="vendor_registration_form"
                  noValidate
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Typography sx={{ fontWeight: "bold", mb: 2 }} variant="h6">
                    {t(
                      "vsupplierregistration.vendors.new.fields.basic_details",
                    )}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.full_name",
                        )}
                        name="fullvendorname"
                        readOnly
                        required
                      />
                    </Grid>

                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.email",
                        )}
                        name="email"
                        required
                        type="email"
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormAsyncAutocomplete
                        control={control}
                        fetchOptions={fetchSegments}
                        filters={(searchTerm: string) => ({
                          name: searchTerm,
                        })}
                        getOptionLabel={(option) => option.name}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.main_segment",
                        )}
                        name="segment"
                        queryKey={["segments", "infinity"]}
                      />
                    </Grid>

                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormAsyncAutocomplete
                        control={control}
                        fetchOptions={fetchSegments}
                        filters={(searchTerm: string) => ({
                          name: searchTerm,
                        })}
                        getOptionLabel={(option) => option.name}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.secondary_segment",
                        )}
                        name="segment2"
                        queryKey={["segments", "infinity"]}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.services_description",
                        )}
                        multiline
                        name="descriptionofservices"
                        rows={3}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.website",
                        )}
                        name="website"
                        // type="url"
                      />
                    </Grid>

                    <Grid size={{ md: 3, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.phone",
                        )}
                        name="phone"
                        readOnly
                        type="tel"
                      />
                    </Grid>

                    <Grid size={{ md: 3, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.fax",
                        )}
                        name="fax"
                        readOnly
                        type="tel"
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{ fontWeight: "bold", mb: 2, mt: 4 }}
                    variant="h6"
                  >
                    {t(
                      "vsupplierregistration.vendors.new.fields.address_details",
                    )}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 8, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.address",
                        )}
                        name="address1"
                        readOnly
                        required
                      />
                    </Grid>

                    <Grid size={{ md: 2, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.address_number",
                        )}
                        name="address_number"
                        readOnly
                      />
                    </Grid>

                    <Grid size={{ md: 2, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.address_suppl",
                        )}
                        name="address_suppl"
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.district",
                        )}
                        name="district"
                        readOnly
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.country",
                        )}
                        name="country"
                        readOnly
                        required
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.state",
                        )}
                        name="state_old"
                        readOnly
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.city",
                        )}
                        name="city"
                        readOnly
                        required
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.zip",
                        )}
                        name="postalcode"
                        readOnly
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.po_box",
                        )}
                        name="pobox"
                        readOnly
                      />
                    </Grid>

                    <Grid size={{ md: 4, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.po_postal_code",
                        )}
                        name="popostalcode"
                        readOnly
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{ fontWeight: "bold", mb: 2, mt: 4 }}
                    variant="h6"
                  >
                    {t(
                      "vsupplierregistration.vendors.new.fields.company_details",
                    )}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.sap_code",
                        )}
                        name="sap_code"
                        readOnly
                      />
                    </Grid>

                    <Grid size={{ md: 6, xs: 12 }}>
                      <FormTextField
                        control={control}
                        label={t(
                          "vsupplierregistration.vendors.new.fields.tax_number",
                        )}
                        name="taxnumber1"
                        readOnly
                        required
                      />
                    </Grid>
                  </Grid>

                  <Grid container sx={{ justifyContent: "flex-end", mt: 4 }}>
                    <Grid size="auto">
                      <Button
                        color="success"
                        size="large"
                        type="submit"
                        variant="contained"
                      >
                        {t("common.create")}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )
            )}
          </Box>
        </Paper>
      </Paper>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vsupplierregistration/vendors/new_by_equinor",
)({
  beforeLoad({ context }) {
    if (
      context.auth.user?.roles_names.includes(
        "Vsupplierregistration - Create Vendor",
      )
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  component: RouteComponent,
});
